import { useEffect, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { cn } from '@/lib/utils';
import { useStudioStore } from '@/store/useStudioStore';
import { useCreatorStore, type SelectedElementInfo } from '@/store/useCreatorStore';
import { projectApi } from '@/api/project';
import { fileApi } from '@/api/file';
import { resolveEditableDraft } from '@/lib/draftUtils';
import {
  buildDraftPreviewHtml,
  stripLegacyLandingPageTrackingScript,
  stripLegacyGoogleAdsTrackingScript,
  stripLinkerScript,
} from '@/lib/domUtils';
import { isSpecialProjectType } from '@/lib/projectUtils';
import {
  parseStudioGoogleAdsSettingsFromHtml,
  upsertStudioGoogleAdsInHtml,
} from '@/lib/studioGoogleAds';
import { trackStudioEvent } from '@/lib/studioTracking';
import { getTrackingErrorCode } from '@/lib/trackingUtils';
import type {
  ProjectGoogleAdsConfigVO,
  ProjectGoogleAdsConversionSignalVO,
  ProjectGoogleAdsSignalSourceBindingType,
  SaveGoogleAdsPostbackRequest,
} from '@/types/project';

const TAG_ID_RE = /^AW-\d+$/;
const GOOGLE_ADS_ID_RE = /AW-\d+/;
const GOOGLE_ADS_TAG_CODE_RE =
  /<script\b[\s\S]*?(?:googletagmanager\.com\/gtag\/js\?id=AW-\d+|gtag\(\s*["']config["']\s*,\s*["']AW-\d+["'])[\s\S]*?<\/script>/i;
const LEGACY_GOOGLE_ADS_TEMPLATE_RE =
  /google-ads-tracking|gadsConfig|trackFormSubmission|trackCTAButtonClick|trackTimeOnPage|trackScrollDepth|data-gads/i;

type SignalType = 'form' | 'cta' | 'time' | 'scroll';
type BindableSignalType = Extract<SignalType, 'form' | 'cta'>;
type SignalBindingType = ProjectGoogleAdsSignalSourceBindingType;
type SignalBinding = {
  sourceBindingType?: SignalBindingType;
  sourceEventKey?: string;
  sourceEventLabel?: string;
};
type SignalItem = { uid: string; label: string; type: SignalType } & SignalBinding;
type PersistedSignalItem = Omit<SignalItem, 'uid'>;
type StatusState = { message: string; type: 'ok' | 'warn' };
type ActiveSignalSelectionState = {
  signalId: string;
  signalType: BindableSignalType;
};
type GoogleAdsEditorDraft = {
  tagInput: string;
  connectedTagId: string;
  tagConnected: boolean;
  signals: SignalItem[];
  savedSignals: SignalItem[];
  signalsSaved: boolean;
  selectedSignalType: SignalType;
  dirty: boolean;
};
type ParsedGoogleAdsInput =
  | {
      ok: true;
      configType: 1 | 2;
      displayValue: string;
      tagCode: string;
      tagId?: string;
      trackingTagId: string;
    }
  | {
      ok: false;
      errorCode: string;
      message: string;
      trackingTagId: string;
    };

export interface GoogleAdsSelectionModeState {
  signalType: BindableSignalType;
  signalLabel: string;
  sourceLabel: string;
  title: string;
  stateText: string;
  helperText: string;
}

const SIGNAL_ORDER: SignalType[] = ['cta', 'form', 'time', 'scroll'];
const TIME_ON_PAGE_THRESHOLD_SECONDS = 120;
const SCROLL_DEPTH_THRESHOLD_PERCENT = 75;
const SIGNAL_TYPES_REQUIRING_BINDING = new Set<SignalType>(['form', 'cta']);
const GOOGLE_ADS_EDITOR_DRAFT_CACHE = new Map<string, GoogleAdsEditorDraft>();

const logGoogleAdsDebug = (message: string, details?: Record<string, unknown>) => {
  void message;
  void details;
};

const getTrackingHtmlDebugSnapshot = (html: string) => ({
  length: html.length,
  hasLinkerChildScript: html.includes('linker-child.umd.aiu4qs6n.js'),
  hasStudioGoogleAdsConfig: html.includes('data-cove-studio-gads-config'),
  hasStudioGoogleAdsRuntime: html.includes('data-cove-studio-gads-runtime'),
  hasStudioAnalyticsConfig: html.includes('data-cove-studio-analytics-config'),
  hasStudioAnalyticsRuntime: html.includes('data-cove-studio-analytics-runtime'),
  hasLegacyGoogleTagLoader: /googletagmanager\.com\/gtag\/js/i.test(html),
});

const createSignalUid = () => `gads-signal-${nanoid(10)}`;
const createSignalItem = (signal: PersistedSignalItem): SignalItem => ({
  uid: createSignalUid(),
  ...signal,
});
const toPersistedSignalItem = (signal: SignalItem): PersistedSignalItem => ({
  type: signal.type,
  label: signal.label,
  sourceBindingType: signal.sourceBindingType,
  sourceEventKey: signal.sourceEventKey,
  sourceEventLabel: signal.sourceEventLabel,
});
const cloneSignalItem = (signal: SignalItem): SignalItem => ({ ...signal });
const cloneSignals = (signals: SignalItem[]) => signals.map(cloneSignalItem);

const areSignalItemsEqual = (left: SignalItem, right: SignalItem) =>
  left.type === right.type &&
  left.label === right.label &&
  left.sourceBindingType === right.sourceBindingType &&
  (left.sourceEventKey || '') === (right.sourceEventKey || '') &&
  (left.sourceEventLabel || '') === (right.sourceEventLabel || '');

const areSignalsEqual = (left: SignalItem[], right: SignalItem[]) =>
  left.length === right.length &&
  left.every((signal, index) => areSignalItemsEqual(signal, right[index]));

const buildGoogleAdsEditorDraftKey = (
  currentId: string | null | undefined,
  currentProjectId: string | null | undefined,
  fallbackProjectId?: string | number | null,
) => {
  if (currentId) return `user:${currentId}`;
  if (currentProjectId) return `project:${currentProjectId}`;
  if (fallbackProjectId !== null && fallbackProjectId !== undefined) {
    return `project:${String(fallbackProjectId)}`;
  }
  return null;
};

const cloneGoogleAdsEditorDraft = (draft: GoogleAdsEditorDraft): GoogleAdsEditorDraft => ({
  ...draft,
  signals: cloneSignals(draft.signals),
  savedSignals: cloneSignals(draft.savedSignals),
});

const getGoogleAdsEditorDraft = (key: string | null) => {
  if (!key) return null;
  const draft = GOOGLE_ADS_EDITOR_DRAFT_CACHE.get(key);
  return draft ? cloneGoogleAdsEditorDraft(draft) : null;
};

const setGoogleAdsEditorDraft = (key: string | null, draft: GoogleAdsEditorDraft) => {
  if (!key) return;
  GOOGLE_ADS_EDITOR_DRAFT_CACHE.set(key, cloneGoogleAdsEditorDraft(draft));
};

const SIGNAL_CONFIG: Record<SignalType, { desc: string; label: string; placeholder: string }> = {
  form: {
    label: 'Form Submission',
    desc: 'Fires when a visitor submits any lead form',
    placeholder: 'e.g. lead_form_submit',
  },
  cta: {
    label: 'CTA Button Click',
    desc: 'Fires when the primary CTA is clicked',
    placeholder: 'e.g. primary_cta_click',
  },
  time: {
    label: 'Time on Page (>2 min)',
    desc: 'Signals high-intent visitors to Google',
    placeholder: 'e.g. engaged_visit_120s',
  },
  scroll: {
    label: 'Scroll to 75%',
    desc: 'Visitor reads most of your page content',
    placeholder: 'e.g. scroll_depth_75',
  },
};

const SIGNAL_SOURCE_CONFIG: Record<
  BindableSignalType,
  {
    sourceLabel: string;
    idleLabel: string;
    helperText: string;
  }
> = {
  cta: {
    sourceLabel: 'Click Event',
    idleLabel: 'Select a page element',
    helperText: 'Select the CTA button, link, or its containing block.',
  },
  form: {
    sourceLabel: 'Form Submission Event',
    idleLabel: 'Select a page element',
    helperText: 'Select the form block or a container that contains the form.',
  },
};

const extractGoogleAdsTagId = (value: string) => value.match(GOOGLE_ADS_ID_RE)?.[0] || '';
const normalizeSignalLabel = (value?: string) => value?.trim() || '';
const buildRuntimeSignals = (signals: SignalItem[]) =>
  signals.map((signal) => ({
    type: signal.type,
    label: signal.label.trim(),
    sourceBindingType: signal.sourceBindingType,
    sourceEventKey: signal.sourceEventKey,
    sourceEventLabel: signal.sourceEventLabel,
  }));

const requiresSignalBinding = (type: SignalType): type is BindableSignalType =>
  SIGNAL_TYPES_REQUIRING_BINDING.has(type);
const normalizeBindingValue = (value?: string | null) => value?.trim() || '';

const getBindingTypeLabel = (bindingType?: SignalBindingType) => {
  switch (bindingType) {
    case 'element_id':
      return 'Element ID';
    case 'css_selector':
      return 'CSS Selector';
    case 'gt_event':
      return 'data-gt-event';
    case 'gt_entity':
      return 'data-gt-entity';
    default:
      return 'Not bound';
  }
};

const buildSelectedElementBindingLabel = (selectedElement: SelectedElementInfo) => {
  const textPreview = (selectedElement.bindingTargetText || selectedElement.text)
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 80);
  if (textPreview) return textPreview;
  const bindingNodeName =
    selectedElement.bindingTargetNodeName || selectedElement.nodeName || 'ELEMENT';
  const bindingKey =
    selectedElement.bindingTargetId || selectedElement.bindingTargetSelector || selectedElement.id;
  return `${bindingNodeName.toUpperCase()} ${bindingKey}`;
};

const buildSignalBindingFromSelection = (
  selectedElement: SelectedElementInfo,
): SignalBinding | null => {
  const sourceEventLabel = buildSelectedElementBindingLabel(selectedElement);

  if (selectedElement.bindingTargetSelector) {
    return {
      sourceBindingType: 'css_selector',
      sourceEventKey: selectedElement.bindingTargetSelector,
      sourceEventLabel,
    };
  }

  if (selectedElement.bindingTargetId) {
    return {
      sourceBindingType: 'element_id',
      sourceEventKey: selectedElement.bindingTargetId,
      sourceEventLabel,
    };
  }

  return null;
};

const buildGoogleAdsTagCode = (tagId: string) => `<!-- Global site tag (gtag.js) - Google Ads -->
<script async src="https://www.googletagmanager.com/gtag/js?id=${tagId}"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());

  gtag('config', '${tagId}');
</script>`;

const parseGoogleAdsInput = (value: string): ParsedGoogleAdsInput => {
  const trimmed = value.trim();
  const trackingTagId = extractGoogleAdsTagId(trimmed);

  if (!trimmed) {
    return {
      ok: false,
      errorCode: 'missing_tag_input',
      message: 'Enter a Google Ads Tag ID before connecting.',
      trackingTagId,
    };
  }

  if (TAG_ID_RE.test(trimmed)) {
    return {
      ok: true,
      configType: 2,
      tagId: trimmed,
      tagCode: buildGoogleAdsTagCode(trimmed),
      trackingTagId: trimmed,
      displayValue: trimmed,
    };
  }

  if (LEGACY_GOOGLE_ADS_TEMPLATE_RE.test(trimmed)) {
    return {
      ok: false,
      errorCode: 'legacy_frontend_template',
      message:
        'This legacy frontend template is not supported. Paste an AW- ID or standard Google tag code.',
      trackingTagId,
    };
  }

  if (GOOGLE_ADS_TAG_CODE_RE.test(trimmed) && trackingTagId) {
    return {
      ok: true,
      configType: 1,
      tagCode: trimmed,
      trackingTagId,
      displayValue: trimmed,
    };
  }

  return {
    ok: false,
    errorCode: 'invalid_tag_input',
    message:
      'Invalid Google Ads input. Use AW-XXXXXXXXX or the full Google tag code copied from Google Ads.',
    trackingTagId,
  };
};

const getTagTrackingData = (tagId: string) => {
  const trimmed = tagId.trim();
  return {
    tagIdPrefix: trimmed.slice(0, 3),
    tagIdLength: trimmed.length,
    isFormatValid: TAG_ID_RE.test(trimmed),
  };
};

const getSignalTrackingData = (
  signals: SignalItem[],
  hasTagConnected: boolean,
  missingSignal?: string,
) => ({
  enabledSignals: signals.map((signal) => signal.type),
  enabledSignalCount: signals.length,
  missingSignal,
  hasTagConnected,
});

const buildGoogleAdsProjectParams = (
  currentId: string | number | null | undefined,
  currentProjectId: string | number | null | undefined,
  fallbackProjectId?: string | number | null,
) => {
  if (currentId) return { userProjectId: Number(currentId) };
  if (currentProjectId) return { projectId: Number(currentProjectId) };
  if (fallbackProjectId) return { projectId: Number(fallbackProjectId) };
  return null;
};

const mapProjectSignalTypeToSignalType = (
  signalType: ProjectGoogleAdsConversionSignalVO['signalType'] | undefined,
): SignalType | null => {
  switch (signalType) {
    case 'form':
      return 'form';
    case 'click':
      return 'cta';
    case 'time':
      return 'time';
    case 'scroll':
      return 'scroll';
    default:
      return null;
  }
};

const getFirstSignalOfType = (signals: SignalItem[], type: SignalType) =>
  signals.find((signal) => signal.type === type);

const buildSignalsFromConfig = (
  cfg: ProjectGoogleAdsConfigVO | null,
  fallbackSignals: PersistedSignalItem[] = [],
): SignalItem[] => {
  if (!cfg) return [];

  const apiSignals = Array.isArray(cfg.conversionSignals) ? cfg.conversionSignals : [];
  const normalizedApiSignals = apiSignals.flatMap((signal) => {
    const type = mapProjectSignalTypeToSignalType(signal?.signalType);
    const label = normalizeSignalLabel(signal?.conversionLabel);
    if (!type || !label) return [];

    return [
      createSignalItem({
        type,
        label,
        sourceBindingType: signal?.sourceBindingType,
        sourceEventKey: normalizeBindingValue(signal?.sourceEventKey) || undefined,
        sourceEventLabel: normalizeBindingValue(signal?.sourceEventLabel) || undefined,
      }),
    ];
  });

  if (normalizedApiSignals.length > 0) {
    return normalizedApiSignals;
  }

  const fallbackByType = new Map<SignalType, PersistedSignalItem>();
  fallbackSignals.forEach((signal) => {
    if (!fallbackByType.has(signal.type)) {
      fallbackByType.set(signal.type, signal);
    }
  });

  const buildLegacySignal = (
    type: SignalType,
    enabled: boolean,
    primaryLabel?: string,
  ): SignalItem | null => {
    if (!enabled) return null;

    const fallback = fallbackByType.get(type);
    const label = normalizeSignalLabel(primaryLabel) || normalizeSignalLabel(fallback?.label) || '';
    if (!label) return null;

    return createSignalItem({
      type,
      label,
      sourceBindingType: fallback?.sourceBindingType,
      sourceEventKey: normalizeBindingValue(fallback?.sourceEventKey) || undefined,
      sourceEventLabel: normalizeBindingValue(fallback?.sourceEventLabel) || undefined,
    });
  };

  return [
    buildLegacySignal('form', cfg.conversionFormSubmit === 1, cfg.conversionLabelForm),
    buildLegacySignal(
      'cta',
      cfg.conversionCtaClick === 1 || cfg.conversionClick === 1,
      cfg.conversionLabelCta,
    ),
    buildLegacySignal('time', cfg.conversionTimeOnPage === 1, cfg.conversionLabelTime),
    buildLegacySignal('scroll', cfg.conversionScrollDepth === 1, cfg.conversionLabelScroll),
  ].filter((signal): signal is SignalItem => Boolean(signal));
};

const buildSignalsPayload = (
  projectParams: { userProjectId?: number; projectId?: number },
  signals: SignalItem[],
): SaveGoogleAdsPostbackRequest => {
  const conversionSignals = signals.flatMap<ProjectGoogleAdsConversionSignalVO>((signal) => {
    const label = signal.label.trim();
    if (!label) return [];

    switch (signal.type) {
      case 'form':
        return [
          {
            signalType: 'form',
            sourceBindingType: signal.sourceBindingType,
            sourceEventKey: signal.sourceEventKey,
            sourceEventLabel: signal.sourceEventLabel,
            conversionLabel: label,
          },
        ];
      case 'cta':
        return [
          {
            signalType: 'click',
            sourceBindingType: signal.sourceBindingType,
            sourceEventKey: signal.sourceEventKey,
            sourceEventLabel: signal.sourceEventLabel,
            conversionLabel: label,
          },
        ];
      case 'time':
        return [
          {
            signalType: 'time',
            triggerValue: TIME_ON_PAGE_THRESHOLD_SECONDS,
            triggerUnit: 'second',
            conversionLabel: label,
          },
        ];
      case 'scroll':
        return [
          {
            signalType: 'scroll',
            triggerValue: SCROLL_DEPTH_THRESHOLD_PERCENT,
            triggerUnit: 'percent',
            conversionLabel: label,
          },
        ];
      default:
        return [];
    }
  });

  const firstFormSignal = getFirstSignalOfType(signals, 'form');
  const firstCtaSignal = getFirstSignalOfType(signals, 'cta');
  const firstTimeSignal = getFirstSignalOfType(signals, 'time');
  const firstScrollSignal = getFirstSignalOfType(signals, 'scroll');
  const hasCtaSignals = signals.some((signal) => signal.type === 'cta');

  // Always send the structured array, including an empty array, so deletions clear backend state.
  // Keep legacy fields populated for backends that still validate the older payload shape.
  return {
    ...projectParams,
    conversionFormSubmit: firstFormSignal ? 1 : 0,
    conversionLabelForm: firstFormSignal?.label.trim() || '',
    conversionClick: hasCtaSignals ? 1 : 0,
    conversionCtaClick: hasCtaSignals ? 1 : 0,
    conversionLabelCta: firstCtaSignal?.label.trim() || '',
    conversionTimeOnPage: firstTimeSignal ? 1 : 0,
    conversionLabelTime: firstTimeSignal?.label.trim() || '',
    conversionScrollDepth: firstScrollSignal ? 1 : 0,
    conversionLabelScroll: firstScrollSignal?.label.trim() || '',
    conversionSignals,
  } as const;
};

const getTagStatusMessage = (tagConnected: boolean, signalsSaved: boolean, signalCount: number) => {
  if (!tagConnected) return '';
  void signalsSaved;
  void signalCount;
  return 'Tag connected - tracking active';
};

const loadGoogleAdsSourceHtml = async (
  currentId: string | number | null | undefined,
  currentProjectId: string | number | null | undefined,
  usesProjectFileApi: boolean,
) => {
  if (usesProjectFileApi) {
    if (!currentProjectId) return '';

    try {
      const files = await fileApi.listFiles({
        projectId: Number(currentProjectId),
        path: 'dist',
      });
      const indexFile = files.find((file) => file.name === 'index.html' && !file.directory);
      if (!indexFile) return '';

      const targetPath = indexFile.path.startsWith('dist/')
        ? indexFile.path
        : `dist/${indexFile.path}`;
      const fileContent = await fileApi.readFile({
        projectId: Number(currentProjectId),
        path: targetPath,
      });
      return fileContent.content || '';
    } catch {
      return '';
    }
  }

  if (!currentId) return '';

  try {
    const { editableDraft } = await resolveEditableDraft(Number(currentId));
    return editableDraft?.content || '';
  } catch {
    return '';
  }
};

interface StudioSettingsGAdsProps {
  onTagConnectionChange?: (connected: boolean) => void;
  onSelectionModeChange?: (state: GoogleAdsSelectionModeState | null) => void;
}

export const StudioSettingsGAds = ({
  onTagConnectionChange,
  onSelectionModeChange,
}: StudioSettingsGAdsProps = {}) => {
  const currentProjectId = useStudioStore((state) => state.currentProjectId);
  const currentId = useStudioStore((state) => state.currentId);
  const currentProject = useStudioStore((state) => state.currentProject);
  const currentProjectTypeId = useStudioStore((state) => state.currentProjectTypeId);
  const isGadsTagConnected = useStudioStore((state) => state.isGadsTagConnected);
  const selectedElement = useCreatorStore((state) => state.selectedElement);
  const setSelectedElement = useCreatorStore((state) => state.setSelectedElement);
  const setCurrentDraftMeta = useStudioStore((state) => state.setCurrentDraftMeta);
  const setGeneratedHtml = useStudioStore((state) => state.setGeneratedHtml);
  const setGadsTagConnected = useStudioStore((state) => state.setGadsTagConnected);
  const setIframeUrl = useStudioStore((state) => state.setIframeUrl);
  const resolvedProjectTypeId =
    currentProjectTypeId ?? currentProject?.projectTypeId ?? currentProject?.projectType;
  const usesProjectFileApi = isSpecialProjectType(resolvedProjectTypeId);
  const googleAdsProjectParams = buildGoogleAdsProjectParams(
    currentId,
    currentProjectId,
    currentProject?.projectId,
  );
  const googleAdsEditorDraftKey = buildGoogleAdsEditorDraftKey(
    currentId,
    currentProjectId,
    currentProject?.projectId,
  );
  const signalInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [connectedTagId, setConnectedTagId] = useState('');
  const [tagConnected, setTagConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signals, setSignals] = useState<SignalItem[]>([]);
  const [savedSignals, setSavedSignals] = useState<SignalItem[]>([]);
  const [signalsSaved, setSignalsSaved] = useState(false);
  const [tagStatus, setTagStatus] = useState<StatusState | null>(null);
  const [signalsStatus, setSignalsStatus] = useState<StatusState | null>(null);
  const [selectedSignalType, setSelectedSignalType] = useState<SignalType>('form');
  const [activeSelection, setActiveSelection] = useState<ActiveSignalSelectionState | null>(null);
  const [isSignalMenuOpen, setSignalMenuOpen] = useState(false);
  const [hasResolvedSavedTagState, setHasResolvedSavedTagState] = useState(false);

  useEffect(() => {
    const projectParams = buildGoogleAdsProjectParams(
      currentId,
      currentProjectId,
      currentProject?.projectId,
    );

    setHasResolvedSavedTagState(false);
    setTagStatus(null);
    setSignalsStatus(null);
    setSignalMenuOpen(false);
    setActiveSelection(null);
    const cachedDraft = getGoogleAdsEditorDraft(googleAdsEditorDraftKey);

    if (cachedDraft) {
      setTagInput(cachedDraft.tagInput);
      setConnectedTagId(cachedDraft.connectedTagId);
      setTagConnected(cachedDraft.tagConnected);
      setSignals(cachedDraft.signals);
      setSavedSignals(cachedDraft.savedSignals);
      setSignalsSaved(cachedDraft.signalsSaved);
      setSelectedSignalType(cachedDraft.selectedSignalType);
      setHasResolvedSavedTagState(true);
    } else {
      setTagInput('');
      setConnectedTagId('');
      setTagConnected(false);
      setSignals([]);
      setSavedSignals([]);
      setSignalsSaved(false);
      setSelectedSignalType('form');
    }

    if (!projectParams) return;

    Promise.all([
      projectApi.getGoogleAdsDetail(projectParams).catch(() => null),
      loadGoogleAdsSourceHtml(currentId, currentProjectId, usesProjectFileApi),
    ])
      .then(([cfg, sourceHtml]) => {
        const htmlSettings = parseStudioGoogleAdsSettingsFromHtml(sourceHtml);
        const htmlFallbackSignals: PersistedSignalItem[] = (htmlSettings?.signals || []).map(
          (signal) => ({
            type: signal.type,
            label: signal.label.trim(),
            sourceBindingType: signal.sourceBindingType,
            sourceEventKey: normalizeBindingValue(signal.sourceEventKey) || undefined,
            sourceEventLabel: normalizeBindingValue(signal.sourceEventLabel) || undefined,
          }),
        );
        const latestDraft = getGoogleAdsEditorDraft(googleAdsEditorDraftKey);

        if (!cfg && !htmlSettings?.tagId) {
          if (latestDraft?.dirty) {
            setHasResolvedSavedTagState(true);
            return;
          }
          setGadsTagConnected(false);
          onTagConnectionChange?.(false);
          setHasResolvedSavedTagState(true);
          return;
        }

        const savedTagId =
          (cfg
            ? cfg.configType === 2
              ? cfg.tagId || ''
              : extractGoogleAdsTagId(cfg.tagCode || '')
            : '') ||
          htmlSettings?.tagId ||
          '';
        const nextSignals = cfg
          ? buildSignalsFromConfig(cfg, htmlFallbackSignals)
          : htmlFallbackSignals.map(createSignalItem);
        const hasSavedSignals = nextSignals.length > 0;

        if (latestDraft?.dirty) {
          setHasResolvedSavedTagState(true);
          return;
        }

        setTagInput(savedTagId);
        setConnectedTagId(savedTagId);
        setTagConnected(Boolean(savedTagId));
        setSignals(nextSignals);
        setSavedSignals(nextSignals);
        setSignalsSaved(hasSavedSignals);
        setSelectedSignalType(latestDraft?.selectedSignalType || 'form');
        setGadsTagConnected(Boolean(savedTagId));
        onTagConnectionChange?.(Boolean(savedTagId));
        setHasResolvedSavedTagState(true);
        setGoogleAdsEditorDraft(googleAdsEditorDraftKey, {
          tagInput: savedTagId,
          connectedTagId: savedTagId,
          tagConnected: Boolean(savedTagId),
          signals: nextSignals,
          savedSignals: nextSignals,
          signalsSaved: hasSavedSignals,
          selectedSignalType: latestDraft?.selectedSignalType || 'form',
          dirty: false,
        });
      })
      .catch(() => {
        /* ignore load errors */
      });
  }, [
    currentId,
    currentProject?.projectId,
    currentProjectId,
    googleAdsEditorDraftKey,
    usesProjectFileApi,
    onTagConnectionChange,
    setGadsTagConnected,
  ]);

  useEffect(() => {
    if (!googleAdsEditorDraftKey || !hasResolvedSavedTagState) return;

    const dirty =
      tagInput.trim() !== connectedTagId.trim() ||
      !signalsSaved ||
      !areSignalsEqual(signals, savedSignals);

    setGoogleAdsEditorDraft(googleAdsEditorDraftKey, {
      tagInput,
      connectedTagId,
      tagConnected,
      signals,
      savedSignals,
      signalsSaved,
      selectedSignalType,
      dirty,
    });
  }, [
    connectedTagId,
    googleAdsEditorDraftKey,
    hasResolvedSavedTagState,
    savedSignals,
    selectedSignalType,
    signals,
    signalsSaved,
    tagConnected,
    tagInput,
  ]);

  useEffect(() => {
    if (!hasResolvedSavedTagState) return;
    if (tagConnected === isGadsTagConnected) return;
    setGadsTagConnected(tagConnected);
    onTagConnectionChange?.(tagConnected);
  }, [
    hasResolvedSavedTagState,
    isGadsTagConnected,
    onTagConnectionChange,
    setGadsTagConnected,
    tagConnected,
  ]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setSignalMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!onSelectionModeChange || !activeSelection) {
      onSelectionModeChange?.(null);
      return;
    }

    const sourceConfig = SIGNAL_SOURCE_CONFIG[activeSelection.signalType];
    onSelectionModeChange({
      signalType: activeSelection.signalType,
      signalLabel: SIGNAL_CONFIG[activeSelection.signalType].label,
      sourceLabel: sourceConfig.sourceLabel,
      title: 'Select page tag',
      stateText: 'Click any element',
      helperText: sourceConfig.helperText,
    });
  }, [activeSelection, onSelectionModeChange]);

  useEffect(
    () => () => {
      onSelectionModeChange?.(null);
    },
    [onSelectionModeChange],
  );

  useEffect(() => {
    if (!activeSelection || !selectedElement) return;

    const nextBinding = buildSignalBindingFromSelection(selectedElement);
    if (!nextBinding) {
      setSignalsStatus({
        type: 'warn',
        message:
          'The current selection does not expose a stable page tag yet. Select a more specific node and try again.',
      });
      return;
    }

    const signalType = activeSelection.signalType;
    const signalId = activeSelection.signalId;
    setSignals((prev) =>
      prev.map((signal) => (signal.uid === signalId ? { ...signal, ...nextBinding } : signal)),
    );
    setSignalsSaved(false);
    setSignalsStatus({
      type: 'ok',
      message: `${SIGNAL_CONFIG[signalType].label} page tag selected. Add the Conversion Label, then save conversion signals.`,
    });
    setActiveSelection(null);
    requestAnimationFrame(() => signalInputRefs.current[signalId]?.focus());
  }, [activeSelection, selectedElement]);

  const persistGoogleAdsTrackingToSource = async (
    nextConnectedTagId: string,
    nextSignals: SignalItem[],
  ) => {
    if (!currentId && !currentProjectId) return;

    logGoogleAdsDebug('persistGoogleAdsTrackingToSource started', {
      usesProjectFileApi,
      nextConnectedTagId,
      signalCount: nextSignals.length,
      signals: nextSignals.map((signal) => ({
        type: signal.type,
        label: signal.label,
        sourceBindingType: signal.sourceBindingType,
        sourceEventKey: signal.sourceEventKey,
      })),
    });

    const nextTrackingHtml = (html: string) =>
      upsertStudioGoogleAdsInHtml(
        stripLegacyLandingPageTrackingScript(stripLegacyGoogleAdsTrackingScript(html)),
        {
          tagId: nextConnectedTagId.trim(),
          signals: buildRuntimeSignals(nextSignals),
        },
      );

    if (usesProjectFileApi) {
      if (!currentProjectId) return;
      const files = await fileApi.listFiles({
        projectId: Number(currentProjectId),
        path: 'dist',
      });
      const indexFile = files.find((file) => file.name === 'index.html' && !file.directory);
      if (!indexFile) {
        throw new Error('index.html not found in dist.');
      }

      const targetPath = indexFile.path.startsWith('dist/')
        ? indexFile.path
        : `dist/${indexFile.path}`;
      const fileContent = await fileApi.readFile({
        projectId: Number(currentProjectId),
        path: targetPath,
      });
      const updatedContent = nextTrackingHtml(fileContent.content || '');
      const previewHtml = buildDraftPreviewHtml(updatedContent);
      logGoogleAdsDebug('prepared Google Ads tracking update for dist/index.html', {
        targetPath,
        sourceSnapshot: getTrackingHtmlDebugSnapshot(fileContent.content || ''),
        updatedSnapshot: getTrackingHtmlDebugSnapshot(updatedContent),
        previewSnapshot: getTrackingHtmlDebugSnapshot(previewHtml),
      });
      if (updatedContent !== (fileContent.content || '')) {
        await fileApi.writeFile({
          projectId: Number(currentProjectId),
          path: targetPath,
          content: updatedContent,
        });
      }

      setGeneratedHtml(previewHtml);
      setIframeUrl(null);
      logGoogleAdsDebug('switched preview source after dist/index.html update', {
        targetPath,
        iframeUrlCleared: true,
      });
      return;
    }

    if (!currentId) return;

    const { editableDraft, saveBaseVersion } = await resolveEditableDraft(Number(currentId));
    const sourceContent =
      editableDraft?.content || stripLinkerScript(useStudioStore.getState().generatedHtml || '');
    if (!sourceContent.trim()) {
      throw new Error('No page content available to attach Google Ads tracking.');
    }

    const updatedContent = nextTrackingHtml(sourceContent);
    logGoogleAdsDebug('prepared Google Ads tracking draft update', {
      sourceSnapshot: getTrackingHtmlDebugSnapshot(sourceContent),
      updatedSnapshot: getTrackingHtmlDebugSnapshot(updatedContent),
    });
    const savedDraft = await projectApi.saveDraft({
      userProjectId: Number(currentId),
      content: updatedContent,
      changeNote: 'Updated Google Ads tracking',
      draftStatus: 'CONFIRMED',
      baseVersion: saveBaseVersion ?? editableDraft?.version ?? undefined,
    });
    setCurrentDraftMeta(savedDraft.id, savedDraft.version, savedDraft.draftStatus);
    const previewHtml = buildDraftPreviewHtml(savedDraft.content);
    logGoogleAdsDebug('saved Google Ads tracking draft', {
      draftId: savedDraft.id,
      draftVersion: savedDraft.version,
      savedSnapshot: getTrackingHtmlDebugSnapshot(savedDraft.content),
      previewSnapshot: getTrackingHtmlDebugSnapshot(previewHtml),
    });
    setGeneratedHtml(previewHtml);
  };

  const handleTagInputChange = (value: string) => {
    setTagInput(value);
    setTagStatus(null);
  };

  const handleConnect = async () => {
    if (!googleAdsProjectParams) {
      trackStudioEvent('studio_gads_tag_connect_result', {
        result: 'failure',
        ...getTagTrackingData(tagInput),
        errorCode: 'missing_project_id',
      });
      setTagStatus({ type: 'warn', message: 'No project selected.' });
      return;
    }

    const parsedInput = parseGoogleAdsInput(tagInput);
    if (!parsedInput.ok) {
      trackStudioEvent('studio_gads_tag_connect_result', {
        result: 'validation_failed',
        ...getTagTrackingData(parsedInput.trackingTagId || tagInput),
        errorCode: parsedInput.errorCode,
      });
      setTagStatus({ type: 'warn', message: parsedInput.message });
      return;
    }

    setConnecting(true);
    try {
      const savedConfig = await projectApi.saveGoogleAdsTag(
        parsedInput.configType === 1
          ? {
              ...googleAdsProjectParams,
              configType: 1,
              tagCode: parsedInput.tagCode,
            }
          : {
              ...googleAdsProjectParams,
              configType: 2,
              tagId: parsedInput.tagId,
              tagCode: parsedInput.tagCode,
            },
      );
      const savedTagId =
        savedConfig?.configType === 1
          ? extractGoogleAdsTagId(savedConfig?.tagCode || parsedInput.displayValue)
          : savedConfig?.tagId || parsedInput.trackingTagId;
      await persistGoogleAdsTrackingToSource(savedTagId, savedSignals);

      setConnectedTagId(savedTagId);
      setTagInput(savedTagId);
      setTagConnected(true);
      setHasResolvedSavedTagState(true);
      setGadsTagConnected(true);
      onTagConnectionChange?.(true);
      setTagStatus(null);
      trackStudioEvent('studio_gads_tag_connect_result', {
        result: 'success',
        ...getTagTrackingData(savedTagId),
      });
    } catch (error) {
      trackStudioEvent('studio_gads_tag_connect_result', {
        result: 'failure',
        ...getTagTrackingData(parsedInput.trackingTagId || tagInput),
        errorCode: getTrackingErrorCode(error),
      });
      setTagStatus({
        type: 'warn',
        message:
          (error as { message?: string })?.message ||
          'Failed to connect Google Ads. Please try again.',
      });
    } finally {
      setConnecting(false);
    }
  };

  const markSignalsDirty = () => {
    setSignalsSaved(false);
    setSignalsStatus(null);
  };

  const activateSignalSelection = (
    signalId: string,
    type: SignalType,
    reason: 'add' | 'reselect' = 'reselect',
  ) => {
    if (!requiresSignalBinding(type)) return;

    setActiveSelection({ signalId, signalType: type });
    setSelectedElement(null);
    setSignalsStatus({
      type: 'warn',
      message:
        reason === 'add'
          ? `${SIGNAL_CONFIG[type].label} added. Click a page element in the preview area to finish setup.`
          : `Selecting a new page tag for ${SIGNAL_CONFIG[type].label}. Click any element in the preview area to update it.`,
    });
  };

  const clearActiveSignalSelection = () => {
    setActiveSelection(null);
    setSignalsStatus(null);
  };

  const handleSelectedSignalTypeChange = (type: SignalType) => {
    setSelectedSignalType(type);
    setSignalMenuOpen(false);

    // Switching the add-signal type should not keep the node-picker active.
    if (activeSelection) {
      clearActiveSignalSelection();
    }
  };

  const handleAddSignal = () => {
    const nextSignal = createSignalItem({ type: selectedSignalType, label: '' });

    setSignals((prev) => [...prev, nextSignal]);
    setSignalMenuOpen(false);
    markSignalsDirty();

    if (requiresSignalBinding(selectedSignalType)) {
      activateSignalSelection(nextSignal.uid, selectedSignalType, 'add');
      return;
    }

    requestAnimationFrame(() => signalInputRefs.current[nextSignal.uid]?.focus());
  };

  const handleRemoveSignal = (signalId: string) => {
    setSignals((prev) => prev.filter((signal) => signal.uid !== signalId));
    if (activeSelection?.signalId === signalId) {
      setActiveSelection(null);
    }
    markSignalsDirty();
  };

  const handleSignalLabelChange = (signalId: string, value: string) => {
    setSignals((prev) =>
      prev.map((signal) => (signal.uid === signalId ? { ...signal, label: value } : signal)),
    );
    markSignalsDirty();
  };

  const handleBindSignalToSelection = (signalId: string, type: SignalType) => {
    if (!requiresSignalBinding(type)) return;
    setSignalMenuOpen(false);
    activateSignalSelection(signalId, type, 'reselect');
  };

  const handleClearSignalBinding = (signalId: string, type: SignalType) => {
    if (!requiresSignalBinding(type)) return;

    setSignals((prev) =>
      prev.map((signal) =>
        signal.uid === signalId
          ? {
              ...signal,
              sourceBindingType: undefined,
              sourceEventKey: undefined,
              sourceEventLabel: undefined,
            }
          : signal,
      ),
    );
    if (activeSelection?.signalId === signalId) {
      setActiveSelection(null);
    }
    markSignalsDirty();
    setSignalsStatus({
      type: 'warn',
      message: `${SIGNAL_CONFIG[type].label} binding was cleared. Rebind it before saving.`,
    });
  };

  const handleSaveSignals = async () => {
    if (!googleAdsProjectParams) {
      trackStudioEvent('studio_gads_signal_save_result', {
        result: 'failure',
        ...getSignalTrackingData(signals, tagConnected),
        errorCode: 'missing_project_id',
      });
      setSignalsStatus({ type: 'warn', message: 'No project selected.' });
      return;
    }

    const missingSignal = signals.find((signal) => !signal.label.trim());
    if (missingSignal) {
      trackStudioEvent('studio_gads_signal_save_result', {
        result: 'validation_failed',
        ...getSignalTrackingData(signals, tagConnected, missingSignal.type),
        errorCode: 'missing_conversion_label',
      });
      setSignalsStatus({
        type: 'warn',
        message: 'Add a Conversion Label for each added signal before saving.',
      });
      signalInputRefs.current[missingSignal.uid]?.focus();
      setSignalsSaved(false);
      return;
    }

    const missingBindingSignal = signals.find(
      (signal) =>
        requiresSignalBinding(signal.type) &&
        (!signal.sourceBindingType || !normalizeBindingValue(signal.sourceEventKey)),
    );
    if (missingBindingSignal) {
      trackStudioEvent('studio_gads_signal_save_result', {
        result: 'validation_failed',
        ...getSignalTrackingData(signals, tagConnected, missingBindingSignal.type),
        errorCode: 'missing_signal_binding',
      });
      setSignalsStatus({
        type: 'warn',
        message: `${SIGNAL_CONFIG[missingBindingSignal.type].label} must be bound to a preview node before saving.`,
      });
      setSignalsSaved(false);
      return;
    }

    setSaving(true);
    try {
      const result = await projectApi.saveGoogleAdsPostback(
        buildSignalsPayload(googleAdsProjectParams, signals),
      );
      const fallbackSignals = signals.map(toPersistedSignalItem);
      const nextSignals = buildSignalsFromConfig(result, fallbackSignals);
      await persistGoogleAdsTrackingToSource(connectedTagId, nextSignals);

      setSignals(nextSignals);
      setSavedSignals(nextSignals);
      setSignalsSaved(true);
      setSignalsStatus({
        type: 'ok',
        message:
          nextSignals.length === 0
            ? 'Conversion signals saved - postback is currently off until you add a signal.'
            : tagConnected
              ? `${nextSignals.length} conversion signal${nextSignals.length === 1 ? '' : 's'} saved and ready for postback.`
              : `${nextSignals.length} conversion signal${nextSignals.length === 1 ? '' : 's'} saved. Connect your Tag ID to activate postback.`,
      });
      trackStudioEvent('studio_gads_signal_save_result', {
        result: 'success',
        ...getSignalTrackingData(nextSignals, tagConnected),
      });
    } catch (error) {
      trackStudioEvent('studio_gads_signal_save_result', {
        result: 'failure',
        ...getSignalTrackingData(signals, tagConnected),
        errorCode: getTrackingErrorCode(error),
      });
      setSignalsStatus({
        type: 'warn',
        message:
          (error as { message?: string })?.message ||
          'Failed to save conversion signals. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const resolvedTagStatus =
    tagStatus ||
    (tagConnected
      ? {
          type: 'ok' as const,
          message: getTagStatusMessage(tagConnected, signalsSaved, signals.length),
        }
      : null);

  return (
    <div className="cfg-section active animate-in fade-in duration-200">
      <div className="cfg-sec-title mb-[4px] text-[15px] font-[800] tracking-[-0.3px] text-[#0D0D1A]">
        Ad tracking
      </div>
      <div className="cfg-sec-sub mb-[18px] text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
        Connect your ad tag and choose which page actions should be sent back for optimization.
      </div>

      <div className="mb-[14px] rounded-[9px] border border-[#0891B2]/20 bg-[#0891B2]/[0.08] p-[11px_13px] text-[12.5px] leading-[1.6] text-[#0E7490]">
        Enter your Google Ads Tag ID (format: <strong>AW-XXXXXXXXX</strong>). CoveAuto AI will
        automatically inject the tracking code.
      </div>

      <div className="cfg-field mb-[14px]">
        <label className="cfg-label mb-[5px] block text-[12px] font-[700] text-[#0D0D1A]/56">
          Google Ads Tag ID
        </label>
        <div className="flex items-center gap-2">
          <input
            className="cfg-input h-[38px] w-full flex-1 rounded-[8px] border border-[#0D0D1A]/10 px-3 text-[13.5px] transition-all outline-none focus:border-[#6D28D9]/40"
            type="text"
            placeholder="AW-123456789"
            value={tagInput}
            onChange={(event) => handleTagInputChange(event.target.value)}
          />
          <button
            className="cfg-save-btn flex h-[36px] items-center justify-center rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[16px] text-[12.5px] font-[700] whitespace-nowrap text-white hover:opacity-[0.88] disabled:opacity-50"
            onClick={handleConnect}
            disabled={connecting}
          >
            {connecting ? 'Connecting...' : 'Connect'}
          </button>
        </div>
      </div>

      {resolvedTagStatus ? (
        <div className="mb-[14px] flex items-center gap-[8px] rounded-[8px] border border-[rgba(0,0,0,0.07)] bg-[#F7F7FC] px-[12px] py-[10px] text-[12.5px] font-[600] text-[#0D0D1A]">
          <span
            className={cn(
              'h-[8px] w-[8px] shrink-0 rounded-full',
              resolvedTagStatus.type === 'ok' ? 'bg-emerald-600' : 'bg-[#D97706]',
            )}
          />
          <span>{resolvedTagStatus.message}</span>
        </div>
      ) : null}

      <div className="my-[18px] border-t border-[#0D0D1A]/10" />
      <div className="mb-[4px] text-[13px] font-[800] text-[#0D0D1A]">Page actions</div>
      <div className="mb-[14px] text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
        Choose the visitor actions that should be sent back to Google Ads for optimization.
      </div>

      <div className="cfg-field">
        <label className="cfg-label mb-[10px] block text-[12px] font-[700] text-[#0D0D1A]/56">
          Actions to track
        </label>
        <div className="mb-[12px] rounded-[9px] border border-[#0891B2]/20 bg-[#0891B2]/[0.08] p-[11px_13px] text-[12.5px] leading-[1.6] text-[#0E7490]">
          Add an action, then select the matching page element in the preview. Each action needs its
          own Conversion Label.
        </div>

        {activeSelection ? (
          <div className="mb-[12px] flex items-start justify-between gap-[12px] rounded-[10px] border border-[#6D28D9]/20 bg-[#6D28D9]/[0.08] p-[12px_13px] text-[12px] leading-[1.6] text-[#4C1D95]">
            <div className="min-w-0">
              <div className="font-[800]">
                Selecting page tag for{' '}
                {SIGNAL_SOURCE_CONFIG[activeSelection.signalType].sourceLabel}
              </div>
              <div className="mt-[2px] text-[#4C1D95]/80">
                Click any element in the preview area to finish setup, then enter the Conversion
                Label.
              </div>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-[8px] border border-[#6D28D9]/15 bg-white px-[10px] py-[6px] text-[11px] font-[700] text-[#6D28D9] transition-colors hover:bg-[#6D28D9]/[0.04]"
              onClick={clearActiveSignalSelection}
            >
              Stop Selecting
            </button>
          </div>
        ) : (
          <div className="mb-[12px] rounded-[9px] border border-[#0D0D1A]/10 bg-[#F7F7FC] p-[11px_13px] text-[12px] leading-[1.6] text-[#0D0D1A]/60">
            {selectedElement
              ? `Current preview selection: ${buildSelectedElementBindingLabel(selectedElement)}`
              : 'Add a form or CTA action, then select the matching page element without closing the drawer.'}
          </div>
        )}

        <div className="mb-[12px] flex items-center gap-[10px]">
          <div ref={pickerRef} className="relative min-w-0 flex-1">
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={isSignalMenuOpen}
              onClick={() => setSignalMenuOpen((prev) => !prev)}
              className="flex h-[38px] w-full items-center justify-between gap-[10px] rounded-[9px] border border-[#0D0D1A]/10 bg-white px-[12px] text-left text-[13px] font-[600] text-[#0D0D1A] transition-all hover:border-[#6D28D9]/30"
            >
              <span className="truncate">{SIGNAL_CONFIG[selectedSignalType].label}</span>
              <i className="fa-solid fa-chevron-down text-[11px] text-[#0D0D1A]/40" />
            </button>

            {isSignalMenuOpen ? (
              <div className="absolute top-[calc(100%+8px)] left-0 z-20 w-full overflow-hidden rounded-[12px] border border-[#0D0D1A]/10 bg-white p-[6px] shadow-[0_16px_32px_rgba(15,23,42,0.14)]">
                {SIGNAL_ORDER.map((type) => (
                  <button
                    key={type}
                    type="button"
                    role="option"
                    onClick={() => handleSelectedSignalTypeChange(type)}
                    className={cn(
                      'flex w-full items-center rounded-[8px] px-[10px] py-[8px] text-left text-[12.5px] font-[700] transition-colors',
                      selectedSignalType === type
                        ? 'bg-[#6D28D9]/[0.08] text-[#6D28D9]'
                        : 'text-[#0D0D1A]/70 hover:bg-black/[0.03]',
                    )}
                  >
                    {SIGNAL_CONFIG[type].label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <button
            className="cfg-save-btn inline-flex h-[36px] items-center justify-center gap-[6px] rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[14px] text-[12.5px] font-[700] text-white transition-opacity hover:opacity-[0.88]"
            type="button"
            onClick={handleAddSignal}
          >
            <i className="fa-solid fa-plus text-[11px]" />
            Add Signal
          </button>
        </div>

        <div className="space-y-[10px]">
          {signals.length === 0 ? (
            <div className="rounded-[12px] border border-dashed border-[#0D0D1A]/12 bg-[linear-gradient(180deg,rgba(248,250,252,.88),#fff)] px-[14px] py-[16px] text-[12.5px] leading-[1.6] text-[#0D0D1A]/46">
              No conversion signals added yet. Choose a signal type above, then add its Google Ads
              Conversion Label.
            </div>
          ) : (
            signals.map((signal) => (
              <div
                key={signal.uid}
                className={cn(
                  'rounded-[12px] border border-[#0D0D1A]/10 bg-white px-[14px] py-[13px]',
                  activeSelection?.signalId === signal.uid &&
                    'border-[#6D28D9]/35 shadow-[0_16px_32px_rgba(109,40,217,0.12)]',
                )}
              >
                <div className="flex items-start justify-between gap-[12px]">
                  <div className="min-w-0">
                    <div className="text-[13px] font-[700] text-[#0D0D1A]">
                      {SIGNAL_CONFIG[signal.type].label}
                    </div>
                    <div className="mt-[2px] text-[11.5px] text-[#0D0D1A]/40">
                      {SIGNAL_CONFIG[signal.type].desc}
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${SIGNAL_CONFIG[signal.type].label}`}
                    className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[8px] border border-[#0D0D1A]/10 bg-white text-[#0D0D1A]/40 transition-colors hover:border-[#DC2626]/25 hover:bg-[#DC2626]/[0.04] hover:text-[#DC2626]"
                    onClick={() => handleRemoveSignal(signal.uid)}
                  >
                    <i className="fa-solid fa-xmark text-[12px]" />
                  </button>
                </div>

                <div className="mt-[12px]">
                  <label className="cfg-label mb-[5px] block text-[11.5px] font-[700] text-[#0D0D1A]/56">
                    Conversion Label
                  </label>
                  <input
                    ref={(node) => {
                      signalInputRefs.current[signal.uid] = node;
                    }}
                    className="w-full rounded-[8px] border border-[#0D0D1A]/10 bg-white px-[12px] py-[9px] text-[13.5px] transition-colors outline-none focus:border-[rgba(109,40,217,0.4)]"
                    type="text"
                    placeholder={SIGNAL_CONFIG[signal.type].placeholder}
                    value={signal.label}
                    onChange={(event) => handleSignalLabelChange(signal.uid, event.target.value)}
                  />
                  <div className="mt-[6px] text-[11px] text-[#0D0D1A]/40">
                    Use the Google Ads conversion label from the event snippet.
                  </div>
                </div>

                {requiresSignalBinding(signal.type) ? (
                  <div className="mt-[12px]">
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-center gap-[9px] rounded-[10px] border px-[12px] py-[11px] text-left transition-colors',
                        activeSelection?.signalId === signal.uid
                          ? 'border-[#6D28D9]/35 bg-[#6D28D9]/[0.08] text-[#6D28D9]'
                          : 'border-[#0D0D1A]/10 bg-[#F8FAFC] text-[#0D0D1A]',
                      )}
                      onClick={() => handleBindSignalToSelection(signal.uid, signal.type)}
                    >
                      <i className="fa-solid fa-location-dot shrink-0 text-[12px] text-[#6D28D9]" />
                      <span className="min-w-0 text-[11.5px] leading-[1.5]">
                        {SIGNAL_SOURCE_CONFIG[signal.type].sourceLabel}:{' '}
                        <strong className="font-[800] text-[#0D0D1A]">
                          {signal.sourceEventLabel || SIGNAL_SOURCE_CONFIG[signal.type].idleLabel}
                        </strong>
                      </span>
                    </button>

                    <div className="mt-[8px] flex items-start justify-between gap-[12px]">
                      <div className="min-w-0 text-[11px] leading-[1.5] text-[#0D0D1A]/42">
                        {activeSelection?.signalId === signal.uid
                          ? 'Click any element in the preview area to finish setup.'
                          : SIGNAL_SOURCE_CONFIG[signal.type].helperText}
                        {signal.sourceEventKey ? (
                          <span className="block pt-[3px]">
                            {getBindingTypeLabel(signal.sourceBindingType)}: {signal.sourceEventKey}
                          </span>
                        ) : null}
                      </div>
                      {signal.sourceEventKey ? (
                        <button
                          type="button"
                          className="shrink-0 rounded-[8px] border border-[#DC2626]/15 bg-white px-[10px] py-[6px] text-[11px] font-[700] text-[#DC2626] transition-colors hover:bg-[#DC2626]/[0.04]"
                          onClick={() => handleClearSignalBinding(signal.uid, signal.type)}
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>

        <div className="mt-[14px]">
          <button
            className="cfg-save-btn inline-flex h-[36px] cursor-pointer items-center justify-center rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[16px] text-[12.5px] font-[700] text-white transition-opacity hover:opacity-[0.88] disabled:opacity-50"
            onClick={handleSaveSignals}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Conversion Signals'}
          </button>
        </div>

        {signalsStatus ? (
          <div className="mt-[12px] flex items-center gap-[8px] rounded-[8px] border border-[rgba(0,0,0,0.07)] bg-[#F7F7FC] px-[12px] py-[10px] text-[12.5px] font-[600] text-[#0D0D1A]">
            <span
              className={cn(
                'h-[8px] w-[8px] shrink-0 rounded-full',
                signalsStatus.type === 'ok' ? 'bg-emerald-600' : 'bg-[#D97706]',
              )}
            />
            <span>{signalsStatus.message}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};
